/**
 * Chat History Component
 *
 * Shows list of previous chat sessions with options to
 * view, pin, and delete.
 *
 * v2.1 - Fixed click-outside dropdown close, added infinite scroll
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  MessageSquarePlus,
  Pin,
  Trash2,
  MoreVertical,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader,
  AlertTriangle,
  X
} from 'lucide-react';
import { useChatHistory } from '../hooks/useChatHistory';
import { useSettings } from '../hooks/useSettings';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
  cursor: pointer;
  user-select: none;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const SessionList = styled(motion.div)`
  max-height: 300px;
  overflow-y: auto;
  margin-top: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const SessionItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SessionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const SessionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SessionTitle = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  margin-top: 2px;
`;

const PinIndicator = styled.span`
  color: #fbbf24;
  display: flex;
  align-items: center;
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;

  ${SessionItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 100;
  min-width: 150px;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  color: ${props => props.$danger ? '#dc2626' : '#374151'};
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f3f4f6'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
`;

const Spinner = styled(Loader)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Confirmation Modal Styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ConfirmModal = styled(motion.div)`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ConfirmHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
`;

const ConfirmIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const ConfirmBody = styled.div`
  padding: 24px;
`;

const ConfirmMessage = styled.p`
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 15px;
  line-height: 1.5;
`;

const ConfirmSubtext = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 13px;
`;

const ConfirmFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  justify-content: flex-end;
`;

const ConfirmButton = styled(motion.button)`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$danger ? `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background: #f3f4f6;
    }
  `}
`;

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;

  // Today
  if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Yesterday
  if (diff < 48 * 60 * 60 * 1000) {
    return 'Yesterday';
  }

  // Within a week
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }

  // Older
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function ChatHistory({ onSelectSession }) {
  const { language } = useSettings();
  const {
    sessions,
    currentSessionId,
    loading,
    loadingMore,
    hasMore,
    createSession,
    selectSession,
    deleteSession,
    pinSession,
    loadMoreSessions,
  } = useChatHistory();

  const [isExpanded, setIsExpanded] = useState(true);
  const [menuOpen, setMenuOpen] = useState(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Close dropdown when clicking anywhere outside the dropdown menu
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (event) => {
      // Find the dropdown menu element
      const dropdownMenu = document.querySelector('[data-dropdown-menu="true"]');
      const menuButton = event.target.closest('[data-menu-button="true"]');

      // Close if clicking outside the dropdown menu (and not on a menu button)
      if (dropdownMenu && !dropdownMenu.contains(event.target) && !menuButton) {
        setMenuOpen(null);
      }
    };

    // Use setTimeout to avoid immediate trigger from the same click that opened the menu
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuOpen]);

  // Infinite scroll - load more when scrolling near bottom
  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listElement;
      // Load more when within 50px of bottom
      if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loadingMore) {
        loadMoreSessions();
      }
    };

    listElement.addEventListener('scroll', handleScroll);
    return () => listElement.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, loadMoreSessions]);

  const handleNewChat = async (e) => {
    e.stopPropagation();
    const session = await createSession();
    if (session && onSelectSession) {
      onSelectSession(session);
    }
  };

  const handleSelectSession = async (session) => {
    const fullSession = await selectSession(session.id);
    if (fullSession && onSelectSession) {
      onSelectSession(fullSession);
    }
  };

  const handleMenuAction = async (action, session, e) => {
    e.stopPropagation();
    setMenuOpen(null);

    switch (action) {
      case 'pin':
        await pinSession(session.id, !session.is_pinned);
        break;
      case 'delete':
        setDeleteConfirmSession(session);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmSession) {
      await deleteSession(deleteConfirmSession.id);
      setDeleteConfirmSession(null);
    }
  };

  const pinnedSessions = sessions.filter(s => s.is_pinned);
  const recentSessions = sessions.filter(s => !s.is_pinned);

  // Debug logging
  console.log('📋 ChatHistory render - sessions:', sessions.length, 'pinned:', pinnedSessions.length, 'recent:', recentSessions.length);

  return (
    <Container ref={containerRef}>
      <Header onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderTitle>
          <Clock size={16} />
          {language === 'ar' ? 'المحادثات السابقة' : 'Chat History'}
        </HeaderTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <NewChatButton onClick={handleNewChat} title={language === 'ar' ? 'محادثة جديدة' : 'New Chat'}>
            <MessageSquarePlus size={16} />
          </NewChatButton>
          {isExpanded ? <ChevronUp size={16} color="white" /> : <ChevronDown size={16} color="white" />}
        </div>
      </Header>

      <AnimatePresence>
        {isExpanded && (
          <SessionList
            ref={listRef}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {loading && (
              <LoadingState>
                <Spinner size={20} />
              </LoadingState>
            )}

            {!loading && sessions.length === 0 && (
              <EmptyState>
                {language === 'ar'
                  ? 'لا توجد محادثات سابقة'
                  : 'No previous conversations'}
              </EmptyState>
            )}

            {!loading && pinnedSessions.map(session => (
              <SessionItem
                key={session.id}
                $active={session.id === currentSessionId}
                onClick={() => handleSelectSession(session)}
              >
                <SessionIcon $active={session.id === currentSessionId}>
                  <MessageCircle size={16} />
                </SessionIcon>
                <SessionInfo>
                  <SessionTitle>
                    {session.title || (language === 'ar' ? 'محادثة جديدة' : 'New Conversation')}
                  </SessionTitle>
                  <SessionMeta>
                    <PinIndicator><Pin size={10} /></PinIndicator>
                    <span>{session.message_count} {language === 'ar' ? 'رسائل' : 'messages'}</span>
                    <span>{formatDate(session.updated_at)}</span>
                  </SessionMeta>
                </SessionInfo>
                <div style={{ position: 'relative' }}>
                  <MenuButton data-menu-button="true" onClick={(e) => { e.stopPropagation(); setMenuOpen(session.id); }}>
                    <MoreVertical size={14} />
                  </MenuButton>
                  <AnimatePresence>
                    {menuOpen === session.id && (
                      <DropdownMenu
                        data-dropdown-menu="true"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <MenuItem onClick={(e) => handleMenuAction('pin', session, e)}>
                          <Pin size={14} />
                          {language === 'ar' ? 'إلغاء التثبيت' : 'Unpin'}
                        </MenuItem>
                        <MenuItem $danger onClick={(e) => handleMenuAction('delete', session, e)}>
                          <Trash2 size={14} />
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </AnimatePresence>
                </div>
              </SessionItem>
            ))}

            {!loading && recentSessions.map(session => (
              <SessionItem
                key={session.id}
                $active={session.id === currentSessionId}
                onClick={() => handleSelectSession(session)}
              >
                <SessionIcon $active={session.id === currentSessionId}>
                  <MessageCircle size={16} />
                </SessionIcon>
                <SessionInfo>
                  <SessionTitle>
                    {session.title || (language === 'ar' ? 'محادثة جديدة' : 'New Conversation')}
                  </SessionTitle>
                  <SessionMeta>
                    <span>{session.message_count} {language === 'ar' ? 'رسائل' : 'messages'}</span>
                    <span>{formatDate(session.updated_at)}</span>
                  </SessionMeta>
                </SessionInfo>
                <div style={{ position: 'relative' }}>
                  <MenuButton data-menu-button="true" onClick={(e) => { e.stopPropagation(); setMenuOpen(session.id); }}>
                    <MoreVertical size={14} />
                  </MenuButton>
                  <AnimatePresence>
                    {menuOpen === session.id && (
                      <DropdownMenu
                        data-dropdown-menu="true"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <MenuItem onClick={(e) => handleMenuAction('pin', session, e)}>
                          <Pin size={14} />
                          {language === 'ar' ? 'تثبيت' : 'Pin'}
                        </MenuItem>
                        <MenuItem $danger onClick={(e) => handleMenuAction('delete', session, e)}>
                          <Trash2 size={14} />
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </AnimatePresence>
                </div>
              </SessionItem>
            ))}

            {/* Loading more indicator */}
            {loadingMore && (
              <LoadingState>
                <Spinner size={16} />
              </LoadingState>
            )}
          </SessionList>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmSession && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirmSession(null)}
          >
            <ConfirmModal
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ConfirmHeader>
                <ConfirmIcon>
                  <AlertTriangle size={20} />
                </ConfirmIcon>
                <ConfirmTitle>
                  {language === 'ar' ? 'حذف المحادثة' : 'Delete Conversation'}
                </ConfirmTitle>
              </ConfirmHeader>
              <ConfirmBody>
                <ConfirmMessage>
                  {language === 'ar'
                    ? 'هل أنت متأكد من حذف هذه المحادثة؟'
                    : 'Are you sure you want to delete this conversation?'}
                </ConfirmMessage>
                <ConfirmSubtext>
                  {language === 'ar'
                    ? 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف جميع الرسائل نهائياً.'
                    : 'This action cannot be undone. All messages will be permanently deleted.'}
                </ConfirmSubtext>
              </ConfirmBody>
              <ConfirmFooter>
                <ConfirmButton
                  onClick={() => setDeleteConfirmSession(null)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </ConfirmButton>
                <ConfirmButton
                  $danger
                  onClick={handleConfirmDelete}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Trash2 size={14} style={{ marginRight: language === 'ar' ? 0 : 6, marginLeft: language === 'ar' ? 6 : 0 }} />
                  {language === 'ar' ? 'حذف' : 'Delete'}
                </ConfirmButton>
              </ConfirmFooter>
            </ConfirmModal>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
}

export default ChatHistory;
